import moment from "moment";

export type Sport = {
  id: number;
  title: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  summary?: string | null;
  description?: string | null;
};

export type Team = {
  title: string;
  sport: Sport;
};

export type Location = {
  name: string;
};

export type SportingEvent = {
  id: number;
  teamId: number;
  locationId: number;
  year: string;
  start: string;
  createdAt: string;
  updatedAt: string;
  scanCoverage: string | null;
  la1tvCoverageLevel: string | null;
  bailriggfmCoverage: string | null;
  warmupLocationId: number | null;
  warmupAt: string | null;
  note: string | null;
  watch: string | null;
  team: Team;
  location: Location;
  point: { amount: number };
};

export enum LiveMoment {
  COMMENTARY = 1,
  ANNOUNCEMENT = 2,
  GOAL = 3,
  FULL_TIME = 4,
  HALF_TIME = 5,
  KICK_OFF = 6,
  QUOTE = 7,
  LANCS_QUOTE = 8,
  YORK_QUOTE = 9,
  IMAGE = 10,
  TWEET = 11,
  LINK = 12,
  FIRST_QUARTER = 13,
  THIRD_QUARTER = 14,
  SCORE = 15,
}

export type Feed = {
  id: number;
  timetableEntryId: number;
  liveMomentTypeId: LiveMoment;
  author: string;
  scoreGameYork: string;
  scoreRosesYork?: any;
  scoreGameLancs: string;
  scoreRosesLancs?: any;
  text: string;
  pictureFile?: any;
  createdAt: string;
  updatedAt: string;
  team: { title: string; sport: { id: number; title: string } };
  liveMomentType: { name: string };
};

export const getSports = async (token: string): Promise<Sport[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/sports`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch sports");
  } else {
    return response.json();
  }
};

export const getTimetable = async (token: string): Promise<SportingEvent[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/timetable`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch timetable");
  } else {
    const events: SportingEvent[] = await response.json();
    return events.map((event) => ({
      ...event,
      start: moment(event.start).subtract(1, "hour").toISOString(),
    }));
  }
};

export const getFeed = async (token: string): Promise<Feed[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/livefeed/since/1`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch feed");
  } else {
    return await response.json();
  }
};

export const getScore = async (
  token: string
): Promise<{ lancs: number; york: number }> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/score`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch score");
  } else {
    return await response.json();
  }
};

export const getUserDetails = async (token: string) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/userservice/api/UserDetails`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch user details");
  } else {
    return await response.json();
  }
};

export const getSubscriptions = async (token: string, username: string) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/user/get-subscriptions?username=${username}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch score");
  } else {
    const data: { id: number; name: string }[] = await response.json();
    return data.map((d) => d.id);
  }
};

type SportSubscriptionsDTO = {
  sportIds: number[];
};

export const setSubscriptions = async (
  token: string,
  username: string,
  data: SportSubscriptionsDTO
) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/roses/user/set-subscriptions?username=${username}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch score");
  } else {
    return await response.json();
  }
};
